import { Shield, Users, Clock, Database, Lock, Eye } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { FAQ } from "@/components/shared/FAQ";
import { SEO } from "@/components/seo/SEO";

const securityFeatures = [
  {
    icon: Database,
    title: "Arquitetura Multi-tenant",
    description: "Dados de cada cliente são completamente isolados em bancos separados. Não há risco de vazamento entre contas.",
    color: "from-blue-500 to-cyan-500",
  },
  {
    icon: Users,
    title: "Controle de Acesso (RBAC)",
    description: "Permissões granulares por usuário, função e recurso. Você decide exatamente o que cada pessoa pode ver e fazer.",
    color: "from-violet-500 to-purple-500",
  },
  {
    icon: Clock,
    title: "Trilha de Auditoria Completa",
    description: "Todas as ações são registradas com data, hora e usuário. Rastreie qualquer operação a qualquer momento.",
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: Database,
    title: "Backups Automáticos",
    description: "Backups diários com retenção de 30 dias. Recuperação rápida em caso de necessidade.",
    color: "from-orange-500 to-amber-500",
  },
  {
    icon: Lock,
    title: "Criptografia de Dados",
    description: "Dados criptografados em trânsito (TLS 1.3) e em repouso (AES-256). Suas informações sempre protegidas.",
    color: "from-pink-500 to-rose-500",
  },
  {
    icon: Eye,
    title: "Privacidade e LGPD",
    description: "Ferramentas para gestão de consentimento e solicitações de titulares. Conformidade com a Lei Geral de Proteção de Dados.",
    color: "from-slate-600 to-slate-800",
  },
];

const faqItems = [
  {
    question: "Onde os dados são armazenados?",
    answer: "Os dados são armazenados em servidores localizados no Brasil, com redundância geográfica para garantir alta disponibilidade e conformidade com legislação local.",
  },
  {
    question: "A Cogni possui certificações de segurança?",
    answer: "Seguimos as melhores práticas de segurança do mercado. Estamos em processo de certificação SOC 2 Type II. Entre em contato para mais detalhes sobre nossas políticas de segurança.",
  },
  {
    question: "Como funciona o controle de acesso?",
    answer: "Você pode criar perfis de acesso com permissões específicas para cada funcionalidade. Por exemplo: analistas podem ver dados, mas não excluir; gestores podem aprovar rateios; administradores têm acesso total.",
  },
  {
    question: "Os dados podem ser exportados?",
    answer: "Sim, você pode exportar seus dados a qualquer momento em formatos padrão (CSV, JSON, PDF). Garantimos portabilidade total conforme exigido pela LGPD.",
  },
  {
    question: "O que acontece se eu cancelar o serviço?",
    answer: "Você terá acesso aos seus dados por 30 dias após o cancelamento para exportação. Após esse período, os dados são permanentemente excluídos de nossos sistemas.",
  },
];

export default function Seguranca() {
  return (
    <Layout>
      <SEO path="/seguranca" title="Segurança — Cogni" description="Como a Cogni protege seus dados: criptografia, controle de acesso, conformidade LGPD e monitoramento contínuo." />
      <PageHero
        title="Segurança e Compliance"
        subtitle="Seus dados protegidos com as melhores práticas do mercado. Transparência total sobre como armazenamos e processamos informações."
      />

      {/* Security Features */}
      <section className="section-padding section-alt">
        <div className="container-wide">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-headline font-bold mb-4">
              Proteção em todas as camadas
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto">
              Implementamos controles de segurança rigorosos para proteger os dados da sua operação e dos seus clientes.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {securityFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-background border border-border rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}>
                  <feature.icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="section-padding section-gradient-blue relative overflow-hidden">
        <div className="absolute top-0 right-0 w-60 sm:w-80 h-60 sm:h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-sm font-medium mb-4">
                Conformidade
              </span>
              <h2 className="text-headline font-bold mb-4 sm:mb-6">
                Conformidade com LGPD
              </h2>
              <p className="text-muted-foreground mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg">
                A Cogni foi desenvolvida com privacidade em mente desde o início. 
                Oferecemos ferramentas para ajudar sua empresa a cumprir todas as 
                obrigações da Lei Geral de Proteção de Dados.
              </p>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-3 bg-background/60 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div>
                    <strong className="text-sm sm:text-base">Gestão de consentimento</strong>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      Registre e gerencie o consentimento dos titulares de dados.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 bg-background/60 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div>
                    <strong className="text-sm sm:text-base">Atendimento a solicitações</strong>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      Ferramentas para responder a pedidos de acesso, correção e exclusão.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 bg-background/60 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-border">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div>
                    <strong className="text-sm sm:text-base">Relatório de impacto</strong>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      Documentação de processos e medidas de segurança implementadas.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-background/80 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-border p-6 sm:p-8 shadow-xl">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg">
                  <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2">Política de Segurança</h3>
                <p className="text-muted-foreground text-sm mb-4 sm:mb-6">
                  Quer conhecer nossas políticas em detalhes? Solicite nosso documento 
                  de segurança completo.
                </p>
                <a
                  href="/contato"
                  className="inline-flex items-center text-primary hover:underline font-medium text-sm sm:text-base"
                >
                  Solicitar documentação →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        items={faqItems}
        title="Perguntas sobre segurança"
        subtitle="Respostas para as dúvidas mais comuns sobre proteção de dados"
      />

      <CTASection
        variant="dark"
        title="Quer saber mais sobre nossa segurança?"
        subtitle="Agende uma conversa com nosso time técnico para discutir requisitos específicos."
        primaryCTA="Falar com especialista"
      />
    </Layout>
  );
}
